import { skyTerrainCombatCeiling } from './87-sky-terrain.mjs';
/** Physical world authority: one world unit is one metre, Y is up, +Z points
 * toward the rider. Dimensions are neutral authored mesh bounds (width/height/
 * length), excluding optional equipment, glow and formation escorts. Animation
 * envelopes below include articulation and root bank, not camera perspective. */
const asset = (width, height, length, modelScale, reference) =>
  Object.freeze({
    width,
    height,
    length,
    modelScale,
    reference,
  });
const pilotScale = 1.8 / 2.708606267746344;
/** Validated encounter camera-ray limits in metres. A canonical rigid flight
 * frame and explicit plane depth permit wide first-person viewport corners;
 * ordinary flight keeps its existing tighter lateral validation. */
export const SKY_FLIGHT_AIM = Object.freeze({
  depth: 96,
  maxDepth: 130,
  normalAcross: 120,
  framedAcross: 320,
});
export const SKY_WORLD_ASSETS = Object.freeze({
  dolphin: asset(
    1.479456513283643,
    2.284224645068993,
    4.0000003803746225,
    1,
    'Actual38 Swim first pose including raised mounted0.28m navigation lens; animated envelopes are separately verified',
  ),
  gildedLeviathan: asset(
    22,
    18,
    60,
    1,
    'Accepted hollow blockout hull; fins span40m; final exported artwork must fit this metre contract',
  ),
  pilot: asset(
    1.5918987857 * pilotScale,
    1.8,
    0.6930205132 * pilotScale,
    pilotScale,
    'Neutral sole to helmet; equipment follows the same skeleton scale',
  ),
  mammoth: asset(
    2.672278,
    3.5,
    5.610412,
    1,
    'Neutral sole to shoulder/armor crest; gait maximum height3.53m',
  ),
  walker: asset(4.647153378, 4, 5.31508255, 1, 'Neutral four-foot stance to sensor crest'),
  dragon: asset(
    18,
    (5.6796870729 * 18) / 15.9921852907,
    (13.2671881085 * 18) / 15.9921852907,
    18 / 15.9921852907,
    'Nine metres per wing:18m neutral span and6.393m neutral overall height; animated wing-tip height varies by pose',
  ),
  leviathan: asset(
    15.0010233 * 0.65,
    4.0739694 * 0.65,
    19.4201045 * 0.65,
    0.65,
    'Neutral wingspan, underside to crown, snout to tail',
  ),
  twin: asset(
    15.0010233 * 0.7,
    3.8472999 * 0.7,
    18.7144091 * 0.7,
    0.7,
    'Neutral shared wing/body envelope',
  ),
  skeletal: asset(
    14.6880159 * 0.65,
    5.4739694 * 0.65,
    17.5320525 * 0.65,
    0.65,
    'Neutral bony wings and tail; twin formation adds lateral separation',
  ),
  titan: asset(
    6.3349795 * 0.9,
    5.844802 * 0.9,
    2.3699999 * 0.9,
    0.9,
    'Neutral feet to crest; root is torso centre',
  ),
  mask: asset(
    6.31335 * 0.85,
    6.9847474 * 0.85,
    2.8897339 * 0.85,
    0.85,
    'Neutral lowest stone limb to crest; root is sternum',
  ),
  oracle: asset(
    5.8754835 * 1.05,
    4.5726881 * 1.05,
    2.4768095 * 1.05,
    1.05,
    'Six-arm neutral envelope; root is lens/head hinge',
  ),
  sentinel: asset(
    9.8191261 * 0.8,
    5.0252652 * 0.8,
    1.9000001 * 0.8,
    0.8,
    'Neutral mechanical arm spread; root is lens hinge',
  ),
  nautilus: asset(
    3.8292934 * 0.65,
    4.53391 * 0.65,
    2.5148274 * 0.65,
    0.65,
    'Leader mantle and tentacles; orbiting formation clearance is separate',
  ),
  carrier: asset(
    13.569047 * 0.8,
    3.4586508 * 0.8,
    4.2 * 0.8,
    0.8,
    'Neutral turbine-to-turbine span',
  ),
  drone: asset(
    2.8154011 * 0.68,
    0.9422748 * 0.68,
    2.528 * 0.68,
    0.68,
    'Base fighter; domain size multiplier defines squad variants',
  ),
  manta: asset(
    4.114418 * 0.58,
    0.5985627 * 0.58,
    3.9751804 * 0.58,
    0.58,
    'Neutral wing spread; domain size multiplier defines squad variants',
  ),
  gunship: asset(3.1740854 * 0.68, 1.0323228 * 0.68, 2.6962 * 0.68, 0.68, 'Base armored fighter'),
  ace: asset(3.6056113 * 0.68, 0.9422748 * 0.68, 3.315 * 0.68, 0.68, 'Base interceptor'),
});
/** Accepted Leviathan spatial blockout: hull-local Y up, mouth at+Z30.
 * Interior clearance excludes machinery. Final art must preserve these openings. */
/** Physical cruising altitude above the accepted dry island terrain. */
export const SKY_LEVIATHAN_FLIGHT = Object.freeze({ cruiseY: 88, hullZ: -40 });

export const SKY_LEVIATHAN_SPACE = Object.freeze({
  finSpan: 40,
  mouthZ: 30,
  mouthWidth: 8,
  mouthHeight: 7,
  throatStartZ: 23,
  throatEndZ: 7,
  throatWidth: 6,
  throatHeight: 5,
  chamberWidth: 10,
  chamberHeight: 9,
  chamberFrontZ: 7,
  chamberBackZ: -5,
  breachX: 11,
  breachWidth: 8,
  breachHeight: 7,
  routeLength: 46 + 2 * Math.PI,
  aimPlaneDepth: SKY_FLIGHT_AIM.depth,
  routeBendRadius: 4,
  interiorLateral: 0.9,
  interiorVertical: 0.55,
  // Actual maximum-equipment pilot envelope measured at1.26033m, rounded
  // outward so traversal clearance includes the real cannon and backpack.
  pilotEnvelopeRadius: 1.27,
  machineryGapHalfWidth: 1.7,
});
/** Decorative industrial structures in metres. Their full outer galleries
 * stay beyond the Leviathan's exterior route; they never become invisible
 * gameplay obstacles. Ground seating uses the same terrain owner87. */
export const SKY_INDUSTRIAL_SCENERY = Object.freeze({
  modelHeight: 44,
  modelWidth: 10.305097579956055,
  modelDepth: 9.701657772064209,
  heights: Object.freeze([32, 44, 56]),
  positions: Object.freeze(
    [
      [-82, -22],
      [86, -48],
      [-96, -98],
      [104, -136],
      [-78, -185],
      [84, -210],
      [-28, -160, true],
      [-12, -185, true],
      [6, -170, true],
      [22, -210, true],
      [-39, -155, true, 14],
      [-22, -150, true, 18],
      [-5, -153, true, 12],
      [12, -148, true, 18],
      [30, -159, true, 14],
      [-34, -178, true, 20],
      [-18, -173, true, 14],
      [-1, -170, true, 22],
      [15, -177, true, 16],
      [34, -185, true, 18],
      [-28, -205, true, 16],
      [-8, -202, true, 20],
      [9, -202, true, 15],
      [28, -208, true, 14],
    ].map(([x, z, distant = false, height]) =>
      Object.freeze({ x, z, distant, ...(height === undefined ? {} : { height }) }),
    ),
  ),
});
export const SKY_WORLD = Object.freeze({
  groundY: 0,
  terrainY: -0.025,
  corridorWidth: 30,
  lateralLimit: 12,
  pilotAimHeight: 1.4 * pilotScale,
  // Median measured provider Flight eye above the canonical aim reference;
  // breathing/head motion varies by about12cm around this authoring reference.
  pilotFlightEyeHeight: 0.6144,
  pilotSoleOffset: 0,
  pilotContactRadius: 0.65 * pilotScale,
  pilotGrazeRadius: 1.4 * pilotScale,
  maximumAimHeight: 9,
  pilotZ: 1,
  airborneClearance: 0.25,
  sceneryClearanceX: 15,
});
const bossModel = Object.freeze({
  serpent: 'leviathan',
  twin: 'twin',
  skeletalSerpent: 'skeletal',
  skeletalTwin: 'skeletal',
  stoneOracle: 'mask',
  mech: 'titan',
  solarTitan: 'titan',
  oracle: 'sentinel',
  swarm: 'nautilus',
  shellSwarm: 'nautilus',
  carrier: 'carrier',
});
// Lowest sampled animated mesh point relative to root, including formation
// escorts/root bank, with extra margin. Tests sample actual GLBs against these
// conservative envelopes; new art or motion must update the authority together.
const drop = Object.freeze({
  dragon: 4.15,
  leviathan: 5.2,
  twin: 5.2,
  skeletal: 5.2,
  titan: 3.85,
  mask: 4.35,
  oracle: 3.05,
  sentinel: 2.95,
  nautilus: 7.4,
  carrier: 4.3,
});
/** Resolve a boss's physical model scale and safe root altitude. Unknown profiles
 * retain the serpent fallback, matching anatomy83; design1 owns the new Oracle. */
export function skyBossWorldScale(profile, design, artVariant) {
  const model =
    artVariant === 'first-dragon'
      ? 'dragon'
      : design === 1
        ? 'oracle'
        : (bossModel[profile] ?? 'leviathan');
  const descriptor = SKY_WORLD_ASSETS[model];
  const sourceScale = ['leviathan', 'twin', 'skeletal'].includes(model)
    ? 2
    : model === 'titan'
      ? 1.6
      : model === 'dragon'
        ? 1
        : 1.5;
  return {
    model,
    ...descriptor,
    contactScale: descriptor.modelScale / sourceScale,
    minimumRootY:
      SKY_WORLD.groundY +
      skyTerrainCombatCeiling() +
      drop[model] * descriptor.modelScale +
      SKY_WORLD.airborneClearance,
  };
}
/** C2-continuous physical clearance correction. Returns position and velocity
 * together so rendering, hit sockets, projectile emission and steering agree. */
export function skyClearBossGround(y, vy, profile, design, artVariant) {
  const floor = skyBossWorldScale(profile, design, artVariant).minimumRootY;
  const blend = 0.6,
    t = (y - floor) / blend;
  if (t <= 0) return { y: floor, vy: 0 };
  if (t >= 1) return { y, vy };
  return {
    y: floor + blend * (6 * t ** 3 - 8 * t ** 4 + 3 * t ** 5),
    vy: vy * (18 * t ** 2 - 32 * t ** 3 + 15 * t ** 4),
  };
}
/** Pilot root is a sole-relative reference; state.y remains the canonical aim
 * plane. The neutral sole correction is measured from the actual skinned GLB. */
export function skyPilotRootY(aimY, groundY = SKY_WORLD.groundY) {
  return Math.max(groundY, aimY - SKY_WORLD.pilotAimHeight) + SKY_WORLD.pilotSoleOffset;
}

const scenery = (
  template,
  dimensions,
  count,
  baseScale,
  variation,
  axisScale,
  sideOffset,
  groundOffset,
  reference,
) => {
  const [sourceWidth, sourceHeight, sourceLength] = dimensions;
  const minimum = Math.min(...variation),
    maximum = Math.max(...variation);
  return Object.freeze({
    template,
    sourceWidth,
    sourceHeight,
    sourceLength,
    count,
    baseScale,
    variation: Object.freeze(variation),
    axisScale: Object.freeze(axisScale),
    sideOffset,
    groundOffset,
    minWidth: sourceWidth * baseScale * minimum * axisScale[0],
    minHeight: sourceHeight * baseScale * minimum * axisScale[1],
    minLength: sourceLength * baseScale * minimum * axisScale[2],
    maxWidth: sourceWidth * baseScale * maximum * axisScale[0],
    maxHeight: sourceHeight * baseScale * maximum * axisScale[1],
    maxLength: sourceLength * baseScale * maximum * axisScale[2],
    reference,
  });
};
const archDimensions = [7.0988881588, 6.5193920135, 1.7000000477];
const rockDimensions = [3.8300991058, 1.7076033354, 7.5];
const obeliskDimensions = [2.284673214, 6.7070030123, 2.284673214];
export const SKY_WORLD_OBSTACLE_RADII = Object.freeze([1.1, 1.4, 1.7]);
const obstacleVariants = (dimensions) =>
  SKY_WORLD_OBSTACLE_RADII.map((radius) => radius / (Math.hypot(...dimensions) / 2));
/** Measured Blender mesh dimensions and actual full-size instance ranges. Distant
 * emergence scales0→1 inside fog; these ranges describe fully emerged scenery.
 * count0 denotes collision-bearing obstacle templates, not an instanced field. */
export const SKY_WORLD_SCENERY = Object.freeze({
  arch: scenery(
    'arch',
    archDimensions,
    8,
    1.25,
    [0.8, 1.03, 1.26],
    [1, 1, 1],
    31,
    0,
    'Side architecture; yaw reserves the full rotated footprint outside the corridor',
  ),
  rock: scenery(
    'rock',
    rockDimensions,
    36,
    3.4,
    [0.8, 1.03, 1.26],
    [1, 1, 1],
    25,
    0,
    'Large side geology; all yaw orientations clear the walkable corridor',
  ),
  tunnel: scenery(
    'arch',
    archDimensions,
    8,
    3.6,
    [1],
    [7 / 3.6, 1, 1],
    0,
    0,
    'Overhead portal; only X span is stretched to bridge the flight corridor',
  ),
  tree: scenery(
    'tree',
    [4.8777153492, 4.803858012, 4.8660924435],
    20,
    1.8,
    [0.8, 1.03, 1.26],
    [1, 1, 1],
    22,
    -1,
    'Roots intentionally embed one metre below ground; crown and branches stay outside corridor',
  ),
  mushroom: scenery(
    'mushroom',
    [4.3626036644, 4.5258585736, 4.3626036644],
    24,
    2.1,
    [0.8, 1.03, 1.26],
    [1, 1, 1],
    20,
    0,
    'Side fungal columns; measured bulb and cap envelope reserves clearance',
  ),
  obelisk: scenery(
    'obelisk',
    obeliskDimensions,
    0,
    1,
    obstacleVariants(obeliskDimensions),
    [1, 1, 1],
    0,
    0,
    'Destructible ruin; three bounding-sphere radii normalize real mesh dimensions, base rests on ground',
  ),
  rockObstacle: scenery(
    'rock',
    rockDimensions,
    0,
    1,
    obstacleVariants(rockDimensions),
    [1, 1, 1],
    0,
    0,
    'Destructible low rock; three bounding-sphere radii normalize real mesh dimensions, base rests on ground',
  ),
});
/** Deterministic full-size XYZ instance scale. Unknown keys return null, allowing
 * callers to reject stale art keys instead of silently inventing dimensions. */
export function skySceneryScale(key, variant = 0) {
  const descriptor = SKY_WORLD_SCENERY[key];
  if (!descriptor) return null;
  const index =
    (((Math.trunc(variant) || 0) % descriptor.variation.length) + descriptor.variation.length) %
    descriptor.variation.length;
  return descriptor.axisScale.map(
    (value) => value * descriptor.baseScale * descriptor.variation[index],
  );
}
/** Physical obstacle bounds after the exact diagonal-radius normalization used
 * for collision. Invalid radii return null; unknown forms use the rock fallback. */
export function skyObstacleDimensions(form, radius) {
  if (!Number.isFinite(radius) || radius <= 0) return null;
  const source = SKY_WORLD_SCENERY[form === 'ruin' ? 'obelisk' : 'rockObstacle'];
  const modelScale =
    radius / (Math.hypot(source.sourceWidth, source.sourceHeight, source.sourceLength) / 2);
  return {
    modelScale,
    width: source.sourceWidth * modelScale,
    height: source.sourceHeight * modelScale,
    length: source.sourceLength * modelScale,
  };
}

/** Near-ground metres/second match the actual scaled pilot foot stance; flight
 * uses the authored rail speed. Run/Walk clip durations remain animation-owned. */
export function skyGroundTravelSpeed(locomotion, flightSpeed) {
  return locomotion === 'run'
    ? (4.7882318497 * SKY_WORLD_ASSETS.pilot.modelScale) / (16 / 24)
    : locomotion === 'walk'
      ? (2.3287594318 * SKY_WORLD_ASSETS.pilot.modelScale) / (28 / 24)
      : flightSpeed;
}
